/**
 * Agent run workspace chat pane — planning-style assist shell + ConversationShell.
 */

import React, { useMemo } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';
import {
  ConversationShell,
  type ConversationModelOption,
} from '@/components/ai-studio/conversation';
import {
  ASSIST_PANE_HEADER_CLASS,
  ASSIST_PANE_SHELL_CLASS,
  ASSIST_PANE_TITLE_CLASS,
} from '@/components/ai-studio/assist/assistPaneStyles';
import {
  RUN_WORKSPACE_HINT_STEP_MS,
  RUN_WORKSPACE_INPUT_EXAMPLE_PROMPTS,
} from './runWorkspaceInputExamplePrompts';
import type { ActiveConversationContext, ConversationMessage } from '@/types/aiStudio';

function buildAgentDescriptionTooltip(
  persona: string | null,
  purpose: string | null
): string | undefined {
  const parts = [persona?.trim(), purpose?.trim()].filter(Boolean);
  return parts.length > 0 ? parts.join('\n\n') : undefined;
}

export interface RunWorkspaceChatPaneProps {
  agentName: string;
  persona: string | null;
  purpose: string | null;
  headerActions?: React.ReactNode;
  context: ActiveConversationContext;
  messages: ConversationMessage[];
  sending: boolean;
  error?: string | null;
  onSend: (text: string) => void;
  disabled?: boolean;
  composerPlaceholder?: string;
  emptyStateText?: string;
  sidePanel?: React.ReactNode;
  outputsPanel?: React.ReactNode;
  models?: ConversationModelOption[];
  selectedModelId?: string;
  onModelChange?: (modelId: string) => void;
  loading?: boolean;
  /** Decision-mode / session override banner under the agent header. */
  statusBanner?: React.ReactNode;
  className?: string;
}

export const RunWorkspaceChatPane: React.FC<RunWorkspaceChatPaneProps> = ({
  agentName,
  persona,
  purpose,
  headerActions,
  context,
  messages,
  sending,
  error,
  onSend,
  disabled = false,
  composerPlaceholder,
  emptyStateText,
  sidePanel,
  outputsPanel,
  models,
  selectedModelId,
  onModelChange,
  loading = false,
  statusBanner,
  className = '',
}) => {
  const { t } = useLanguage();
  const descriptionTooltip = useMemo(
    () => buildAgentDescriptionTooltip(persona, purpose),
    [persona, purpose]
  );

  const chatShell = loading ? (
    <p className="px-3 py-2 text-base text-[var(--color-surface-text-secondary)]">
      {t('aiStudio.conversation.loading')}
    </p>
  ) : (
    <ConversationShell
      className="min-h-0 flex-1"
      context={context}
      messages={messages}
      sending={sending}
      error={error}
      onSend={onSend}
      disabled={disabled}
      hideContextIndicator
      paneMode
      composerVariant="pill"
      composerHints={RUN_WORKSPACE_INPUT_EXAMPLE_PROMPTS}
      composerHintStepMs={RUN_WORKSPACE_HINT_STEP_MS}
      composerPlaceholder={composerPlaceholder}
      emptyStateText={emptyStateText}
      sidePanel={sidePanel}
      outputsPanel={outputsPanel}
      models={models}
      selectedModelId={selectedModelId}
      onModelChange={onModelChange}
    />
  );

  return (
    <div className={`${ASSIST_PANE_SHELL_CLASS} ${className}`}>
      <header className={ASSIST_PANE_HEADER_CLASS}>
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <h2 className={ASSIST_PANE_TITLE_CLASS} title={descriptionTooltip ?? agentName}>
            {agentName}
          </h2>
          {descriptionTooltip && (
            <span
              className="shrink-0 text-[var(--color-surface-text-secondary)]"
              title={descriptionTooltip}
              aria-label={t('aiStudio.workspace.agentDescription')}
            >
              <InformationCircleIcon className="h-4 w-4" aria-hidden />
            </span>
          )}
        </div>
        {headerActions}
      </header>
      {statusBanner ? <div className="flex-shrink-0 px-3 pb-2">{statusBanner}</div> : null}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-[var(--color-secondary-bg)]">
        {chatShell}
      </div>
    </div>
  );
};
